// ═══════════════════════════════════════════════════════════════
// Common Operating Picture (COP) — Light mode, modern ops-center
// EMC design system · Recharts · react-grid-layout
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ResponsiveGridLayout as RGLBase } from 'react-grid-layout';
const RGLComponent = RGLBase as any;
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import 'leaflet/dist/leaflet.css';
import './COP.css';
import {
  ResponsiveContainer, PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  RadialBarChart, RadialBar,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON } from 'react-leaflet';
import { Menu, ActionIcon } from '../../components/gab/GabComponents';
import {
  IconSettings, IconPlus, IconTrash, IconGripVertical,
  IconBuildingCommunity, IconCash, IconBackhoe,
  IconAlertTriangle, IconClipboardCheck, IconHeartHandshake,
  IconClock, IconRefresh, IconMaximize, IconLayoutDashboard,
  IconBuildingStore, IconUserPlus, IconReceipt, IconChartBar,
  IconAlertCircle, IconCalendarEvent, IconMap,
  IconTemperature, IconShield, IconUsers, IconSos,
  IconHierarchy, IconArrowsMove,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useShelterStore } from '../../store/shelterStore';
import { useImpactStore } from '../../store/impactStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useHandoffStore } from '../../store/handoffStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useIntakeStore } from '../../store/intakeStore';
import { useBattleRhythmStore } from '../../store/battlerhythmStore';
import { useGeoOpsStore } from '../../store/geoopsStore';
import { usePolling } from '../../hooks/usePolling';
import { fmtDateTime } from '../../lib/dateFormat';

// ── Types ────────────────────────────────────────────────────

interface LayoutItem { i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number; }
type ChartMode = 'stat' | 'bar' | 'donut';
interface WidgetDef {
  id: string; title: string; icon: React.ComponentType<{ size?: number }>; color: string;
  defaultW: number; defaultH: number; modes: ChartMode[];
}

const WIDGETS: WidgetDef[] = [
  // ── IC-priority widgets ────────────────────────────────────
  { id: 'life_safety',    title: 'Life Safety',          icon: IconSos,            color: '#B12525', defaultW: 4, defaultH: 3, modes: ['stat'] },
  { id: 'op_period',      title: 'Operational Period',   icon: IconClock,          color: '#4B3FFF', defaultW: 4, defaultH: 3, modes: ['stat'] },
  { id: 'weather',        title: 'Weather',              icon: IconTemperature,    color: '#0284c7', defaultW: 4, defaultH: 3, modes: ['stat'] },
  { id: 'map_overview',   title: 'Situation Map',        icon: IconMap,            color: '#374151', defaultW: 8, defaultH: 6, modes: ['stat'] },
  { id: 'evac_zones',     title: 'Zone Status',          icon: IconArrowsMove,     color: '#d97706', defaultW: 4, defaultH: 4, modes: ['stat'] },
  { id: 'ics_command',    title: 'Command Structure',    icon: IconHierarchy,      color: '#6366f1', defaultW: 4, defaultH: 4, modes: ['stat'] },
  { id: 'battle_rhythm',  title: "Today's Schedule",     icon: IconCalendarEvent,  color: '#0d9488', defaultW: 4, defaultH: 5, modes: ['stat'] },
  { id: 'resource_status',title: 'Resource / Personnel', icon: IconUsers,          color: '#7c3aed', defaultW: 4, defaultH: 3, modes: ['stat'] },
  // ── Module summary widgets ─────────────────────────────────
  { id: 'incident_stats', title: 'Incident Overview',   icon: IconLayoutDashboard,color: 'var(--color-primary-700)', defaultW: 12, defaultH: 2, modes: ['stat'] },
  { id: 'shelter_occupancy',title:'Shelter Occupancy',  icon: IconBuildingCommunity, color: '#0284c7', defaultW: 4, defaultH: 4, modes: ['stat', 'bar'] },
  { id: 'intake_triage',  title: 'Intake Triage',       icon: IconUserPlus,       color: '#b91c1c', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'business_status',title: 'Business Status',     icon: IconBuildingStore,  color: '#7c2d12', defaultW: 4, defaultH: 3, modes: ['stat', 'bar', 'donut'] },
  { id: 'pa_pipeline',    title: 'PA Phase Pipeline',   icon: IconCash,           color: '#1E40AF', defaultW: 4, defaultH: 3, modes: ['stat', 'bar', 'donut'] },
  { id: 'pa_financial',   title: 'PA Financial Summary',icon: IconCash,           color: '#059669', defaultW: 4, defaultH: 3, modes: ['stat', 'bar'] },
  { id: 'pa_rfr',         title: 'Reimbursement Status',icon: IconReceipt,        color: '#0e7490', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'debris_progress',title: 'Debris Progress',     icon: IconBackhoe,        color: '#d97706', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'impact_summary', title: 'Impact Assessments',  icon: IconAlertTriangle,  color: '#B12525', defaultW: 4, defaultH: 3, modes: ['stat', 'bar', 'donut'] },
  { id: 'volunteers',     title: 'Volunteer Status',    icon: IconHeartHandshake, color: '#7c3aed', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'rollcall',       title: 'RollCall Status',     icon: IconClipboardCheck, color: '#0d9488', defaultW: 4, defaultH: 3, modes: ['stat'] },
  { id: 'activity_feed',  title: 'Recent Activity',     icon: IconClock,          color: '#6b7280', defaultW: 4, defaultH: 5, modes: ['stat'] },
];

const LS_KEY = 'cop-layout-v3';
const LS_WIDGETS = 'cop-active-widgets-v3';
const LS_MODES = 'cop-modes-v3';
const DEFAULT_WIDGETS = [
  'life_safety', 'op_period', 'weather',
  'map_overview', 'evac_zones',
  'ics_command', 'battle_rhythm', 'resource_status',
  'shelter_occupancy', 'intake_triage', 'impact_summary', 'volunteers',
];
const MODE_LABELS: Record<ChartMode, string> = { stat: 'Numbers', bar: 'Bar Chart', donut: 'Donut Chart' };

function loadJson<T>(key: string, fb: T): T { try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : fb; } catch { return fb; } }
const save = (k: string, v: unknown) => localStorage.setItem(k, JSON.stringify(v));

// ── Helpers ──────────────────────────────────────────────────

function fmtMoney(n: number): string {
  return '$' + (n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(0) + 'K' : n.toFixed(0));
}

function computeElapsed(startDate: string | undefined): string {
  if (!startDate) return '';
  const diff = Date.now() - new Date(startDate).getTime();
  if (diff < 0) return '';
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  return `T+${d}D ${h}H`;
}

function computeCountdown(endISO: string): string {
  const diff = new Date(endISO).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m`;
}

// WMO weather code → label + CSS color
function wmoLabel(code: number): { label: string; color: string } {
  if (code === 0) return { label: 'Clear', color: '#d97706' };
  if (code <= 3) return { label: 'Partly Cloudy', color: '#6b7280' };
  if (code <= 48) return { label: 'Foggy', color: '#6b7280' };
  if (code <= 57) return { label: 'Drizzle', color: '#0284c7' };
  if (code <= 67) return { label: 'Rain', color: '#0284c7' };
  if (code <= 77) return { label: 'Snow', color: '#6366f1' };
  if (code <= 82) return { label: 'Showers', color: '#0284c7' };
  if (code <= 86) return { label: 'Snow Showers', color: '#6366f1' };
  if (code <= 95) return { label: 'Thunderstorm', color: '#D33423' };
  return { label: 'Severe T-Storm', color: '#D33423' };
}

function windDir(deg: number): string {
  const dirs = ['N','NE','E','SE','S','SW','W','NW'];
  return dirs[Math.round(deg / 45) % 8];
}

const CHART_GRID = <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />;

// ── Incident Banner ──────────────────────────────────────────

function IncidentBanner() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  if (!activeIncident) {
    return (
      <div className="cop-incident-banner cop-incident-banner--none">
        <IconAlertCircle size={16} style={{ color: '#939598', flexShrink: 0 }} />
        <span className="cop-incident-no-incident">No active incident selected — select an incident from the Incidents module to populate the dashboard.</span>
      </div>
    );
  }
  const statusLabel = activeIncident.status || 'active';
  const statusClass = statusLabel === 'active' ? 'cop-status-chip--error' : statusLabel === 'monitoring' ? 'cop-status-chip--warning' : 'cop-status-chip--neutral';
  const elapsed = computeElapsed(activeIncident.startDate);
  return (
    <div className="cop-incident-banner">
      <div className="cop-incident-banner-left">
        <span className="cop-incident-name">{activeIncident.name}</span>
        <span className={`cop-status-chip ${statusClass}`}>{statusLabel}</span>
        {activeIncident.severity && <span className="cop-severity-chip">{activeIncident.severity}</span>}
        {(activeIncident.type || activeIncident.incidentType) && (
          <span className="cop-type-chip">{activeIncident.type || activeIncident.incidentType}</span>
        )}
      </div>
      <div className="cop-incident-banner-right">
        {activeIncident.commander && (
          <span className="cop-incident-meta">IC: <strong>{activeIncident.commander}</strong></span>
        )}
        {elapsed && (
          <span className="cop-incident-elapsed"><span className="cop-live-dot" />{elapsed}</span>
        )}
      </div>
    </div>
  );
}

// ── KPI Strip ────────────────────────────────────────────────

function KpiStrip() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const shelters = useShelterStore(s => s.shelters);
  const individuals = useIntakeStore(s => s.individuals);
  const assessments = useImpactStore(s => s.assessments);
  const volunteers = useVolunteerStore(s => s.volunteers);
  const projects = usePAGrantsStore(s => s.projects);

  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const iShelters = incId ? shelters.filter(s => s.incidentId === incId) : shelters;
  const openShelters = iShelters.filter(s => s.status === 'open').length;
  const iIndividuals = incId ? individuals.filter(i => i.incidentId === incId) : individuals;
  const activeIntake = iIndividuals.filter(i => i.status === 'active').length;
  const iAssessments = incId ? assessments.filter(a => a.incidentId === incId) : assessments;
  const activeVols = volunteers.filter(v => v.status === 'active').length;
  const pa = projects.filter(p => p.workType !== 'hmgp');
  const oblTotal = pa.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);

  const kpis = [
    { icon: <IconBuildingCommunity size={20} />, color: '#0284c7', value: openShelters, label: 'Open Shelters', sublabel: `${iShelters.length} total` },
    { icon: <IconUserPlus size={20} />,          color: '#b91c1c', value: activeIntake,  label: 'Intake Active',    sublabel: `${iIndividuals.length} total` },
    { icon: <IconAlertTriangle size={20} />,     color: '#B12525', value: iAssessments.length, label: 'Impact Assessments', sublabel: null },
    { icon: <IconHeartHandshake size={20} />,    color: '#7c3aed', value: activeVols,   label: 'Active Volunteers', sublabel: `${volunteers.length} total` },
    { icon: <IconCash size={20} />,              color: '#059669', value: fmtMoney(oblTotal), label: 'PA Obligated', sublabel: `${pa.length} projects`, isMoney: true },
  ];

  return (
    <div className="cop-kpi-strip">
      {kpis.map((kpi) => (
        <div key={kpi.label} className="cop-kpi-card">
          <div className="cop-kpi-icon" style={{ color: kpi.color }}>{kpi.icon}</div>
          <div className="cop-kpi-num" style={{ color: kpi.color, textShadow: `0 0 16px ${kpi.color}40` }}>{kpi.value}</div>
          <div className="cop-kpi-label">{kpi.label}</div>
          {kpi.sublabel && <div className="cop-kpi-sublabel">{kpi.sublabel}</div>}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ── IC-PRIORITY WIDGETS ───────────────────────────────────────
// ══════════════════════════════════════════════════════════════

// ── Life Safety ──────────────────────────────────────────────

function W_LifeSafety() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const individuals = useIntakeStore(s => s.individuals);
  const assessments = useImpactStore(s => s.assessments);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';

  const active = (incId ? individuals.filter(i => i.incidentId === incId) : individuals).filter(i => i.status === 'active');
  const critical = active.filter(i => i.triage === 'var(--color-error)').length;
  const urgent   = active.filter(i => i.triage === '#C68700').length;
  const minor    = active.filter(i => i.triage === 'var(--color-success)').length;

  const ia = incId ? assessments.filter(a => a.incidentId === incId) : assessments;
  const destroyed = ia.filter(a => a.damageCategory === 'destroyed').length;
  const major     = ia.filter(a => a.damageCategory === 'major').length;
  const inaccessible = ia.filter(a => a.damageCategory === 'inaccessible').length;

  return (
    <div className="cop-stack">
      {/* Triage section */}
      <div className="cop-section-label">Intake Triage (Active)</div>
      <div className="cop-ls-grid">
        <div className="cop-ls-cell cop-ls-cell--red">
          <span className="cop-ls-num">{critical}</span>
          <span className="cop-ls-lbl">Critical</span>
        </div>
        <div className="cop-ls-cell cop-ls-cell--yellow">
          <span className="cop-ls-num">{urgent}</span>
          <span className="cop-ls-lbl">Urgent</span>
        </div>
        <div className="cop-ls-cell cop-ls-cell--green">
          <span className="cop-ls-num">{minor}</span>
          <span className="cop-ls-lbl">Minor</span>
        </div>
      </div>
      {/* Structure damage section */}
      <div className="cop-section-label" style={{ marginTop: 4 }}>Structural Impact</div>
      <div className="cop-data-rows">
        {[
          { label: 'Destroyed',      value: destroyed,    color: '#D33423' },
          { label: 'Major Damage',   value: major,        color: '#d97706' },
          { label: 'Inaccessible',   value: inaccessible, color: '#6b7280' },
        ].map(r => (
          <div key={r.label} className="cop-data-row">
            <span className="cop-data-row-label">
              <span className="cop-data-row-pip" style={{ background: r.color }} />{r.label}
            </span>
            <span className="cop-data-row-val">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Operational Period ────────────────────────────────────────

function W_OpPeriod() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const activations = useBattleRhythmStore(s => s.activations);
  const events = useBattleRhythmStore(s => s.events);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const activation = activations.find(a => String(a.incidentId) === incId && a.status === 'active')
    ?? activations.find(a => String(a.incidentId) === incId);

  const opEnd = useMemo(() => {
    if (!activation?.opStart) return null;
    const hours = parseInt(activation.opHours || '12', 10);
    return new Date(new Date(activation.opStart).getTime() + hours * 3600000).toISOString();
  }, [activation]);

  const countdown = opEnd ? computeCountdown(opEnd) : null;
  const expired = opEnd ? new Date(opEnd).getTime() < now : false;

  // Next upcoming event today
  const todayEvents = useMemo(() => {
    const todayStr = new Date().toDateString();
    return events
      .filter(e => String(e.incidentId) === incId && e.status !== 'completed' && e.status !== 'skipped')
      .map(e => {
        const [hh, mm] = (e.scheduledTime || '').split(':').map(Number);
        const t = new Date();
        t.setHours(hh || 0, mm || 0, 0, 0);
        return { ...e, _ts: t.getTime() };
      })
      .filter(e => e._ts > now || new Date(e._ts).toDateString() === todayStr)
      .sort((a, b) => a._ts - b._ts);
  }, [events, incId, now]);

  const nextEvent = todayEvents[0];

  if (!activation && !activeIncident) {
    return (
      <div className="cop-stack cop-empty-state">
        <IconClock size={24} style={{ color: '#ADAFB1' }} />
        <span>No battle rhythm activation found.</span>
      </div>
    );
  }

  return (
    <div className="cop-stack">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <div>
          <div className="cop-section-label">Current Op Period</div>
          {activation ? (
            <>
              <div className="cop-op-hours">{activation.opHours}h ops</div>
              {activation.opStart && (
                <div className="cop-sub">Started {fmtDateTime(activation.opStart)}</div>
              )}
            </>
          ) : (
            <div className="cop-sub">No active activation</div>
          )}
        </div>
        {countdown && (
          <div className={`cop-countdown ${expired ? 'cop-countdown--expired' : ''}`}>
            <div className="cop-countdown-val">{countdown}</div>
            <div className="cop-countdown-lbl">{expired ? 'EXPIRED' : 'remaining'}</div>
          </div>
        )}
      </div>

      {opEnd && (
        <div className="cop-data-row" style={{ marginTop: 2 }}>
          <span className="cop-data-row-label">Op ends</span>
          <span className="cop-data-row-val" style={{ fontSize: 12 }}>
            {new Date(opEnd).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}

      <div className="cop-section-label" style={{ marginTop: 8 }}>Next Event</div>
      {nextEvent ? (
        <div className="cop-next-event">
          <div className="cop-next-event-time">{nextEvent.scheduledTime}</div>
          <div className="cop-next-event-title">{nextEvent.title}</div>
          {nextEvent.location && <div className="cop-sub">{nextEvent.location}</div>}
          {nextEvent.responsible && <div className="cop-sub">{nextEvent.responsible}</div>}
        </div>
      ) : (
        <div className="cop-sub">No upcoming events today</div>
      )}

      {todayEvents.length > 1 && (
        <div className="cop-sub" style={{ marginTop: 2 }}>+{todayEvents.length - 1} more today</div>
      )}
    </div>
  );
}

// ── Weather ──────────────────────────────────────────────────

interface WeatherData {
  temp: number;
  windSpeed: number;
  windDir: number;
  code: number;
  precip: number;
  hourly: { time: string; temp: number; precipProb: number; code: number }[];
}

function W_Weather() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const [wx, setWx] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedFor = useRef<string>('');

  const lat = activeIncident?.latitude ? parseFloat(activeIncident.latitude) : null;
  const lon = activeIncident?.longitude ? parseFloat(activeIncident.longitude) : null;

  const doFetch = useCallback((forceLat: number, forceLon: number) => {
    const key = `${forceLat},${forceLon}`;
    fetchedFor.current = key;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      latitude: String(forceLat), longitude: String(forceLon),
      current: 'temperature_2m,weathercode,windspeed_10m,winddirection_10m,precipitation',
      hourly: 'temperature_2m,precipitation_probability,weathercode',
      temperature_unit: 'fahrenheit', windspeed_unit: 'mph',
      timezone: 'auto', forecast_days: '1',
    });
    fetch(`https://api.open-meteo.com/v1/forecast?${params}`)
      .then(r => r.json())
      .then(d => {
        const c = d.current;
        const nowIdx = d.hourly.time.findIndex((t: string) => new Date(t) >= new Date());
        const hourly = d.hourly.time.slice(nowIdx, nowIdx + 6).map((t: string, i: number) => ({
          time: new Date(t).toLocaleTimeString([], { hour: 'numeric', hour12: true }),
          temp: Math.round(d.hourly.temperature_2m[nowIdx + i]),
          precipProb: d.hourly.precipitation_probability[nowIdx + i],
          code: d.hourly.weathercode[nowIdx + i],
        }));
        setWx({ temp: Math.round(c.temperature_2m), windSpeed: Math.round(c.windspeed_10m),
          windDir: c.winddirection_10m, code: c.weathercode, precip: c.precipitation, hourly });
        setLoading(false);
      })
      .catch(() => { setError('Weather unavailable'); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!lat || !lon) return;
    const key = `${lat},${lon}`;
    if (fetchedFor.current === key) return;
    doFetch(lat, lon);
  }, [lat, lon, doFetch]);

  // Retry on window focus if currently in error state
  useEffect(() => {
    if (!lat || !lon) return;
    function onFocus() { if (error) doFetch(lat!, lon!); }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [lat, lon, error, doFetch]);

  if (!lat || !lon) {
    return (
      <div className="cop-stack cop-empty-state">
        <IconTemperature size={24} style={{ color: '#ADAFB1' }} />
        <span>No coordinates on active incident</span>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="cop-sitrep-skeleton" style={{ padding: 8 }}>
        <div className="cop-sitrep-skeleton-line cop-sitrep-skeleton-line--wide" />
        <div className="cop-sitrep-skeleton-line cop-sitrep-skeleton-line--mid" />
      </div>
    );
  }
  if (error || !wx) {
    return (
      <div className="cop-stack cop-empty-state" style={{ gap: 8 }}>
        <span style={{ color: '#D33423' }}>{error || 'No data'}</span>
        {lat && lon && (
          <button onClick={() => doFetch(lat, lon)} style={{
            fontSize: 12, color: '#4B3FFF', background: 'none', border: '1px solid #E8E6FF',
            borderRadius: 4, padding: '3px 8px', cursor: 'pointer',
          }}>Retry</button>
        )}
      </div>
    );
  }

  const { label, color } = wmoLabel(wx.code);
  return (
    <div className="cop-stack">
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
        <div>
          <span className="cop-num" style={{ color, fontSize: 40 }}>{wx.temp}°</span>
          <span className="cop-num-label">F</span>
        </div>
        <div style={{ paddingBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color }}>{label}</div>
          <div className="cop-sub">{wx.windSpeed} mph {windDir(wx.windDir)}</div>
          {wx.precip > 0 && <div className="cop-sub">{wx.precip.toFixed(1)}" precip</div>}
        </div>
      </div>
      <div className="cop-wx-hourly">
        {wx.hourly.map(h => (
          <div key={h.time} className="cop-wx-hour">
            <div className="cop-wx-hour-time">{h.time}</div>
            <div className="cop-wx-hour-temp" style={{ color: wmoLabel(h.code).color }}>{h.temp}°</div>
            {h.precipProb > 20 && (
              <div className="cop-wx-hour-precip">{h.precipProb}%</div>
            )}
          </div>
        ))}
      </div>
      <div className="cop-sub">Incident location · Open-Meteo</div>
    </div>
  );
}

// ── Battle Rhythm / Today's Schedule ─────────────────────────

function W_BattleRhythm() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const events         = useBattleRhythmStore(s => s.events);
  const incId          = activeIncident?.id ? String(activeIncident.id) : '';

  const SLOT_H        = 40;
  const TIME_W        = 46;
  const DISPLAY_START = 5;
  const DISPLAY_END   = 22;

  const catColors: Record<string, string> = {
    briefing: '#4B3FFF', report: '#0891b2', shift: '#059669',
    external: '#d97706', logistics: '#7c3aed', planning: '#1d4ed8', other: '#6b7280',
  };

  function toMins(t: string) {
    const [h, m] = (t || '0:0').split(':').map(Number);
    return h * 60 + (m || 0);
  }

  const scheduled = useMemo(() => {
    return events
      .filter(e => String(e.incidentId) === incId && e.scheduledTime?.includes(':'))
      .filter(e => { const h = Number(e.scheduledTime.split(':')[0]); return h >= DISPLAY_START && h <= DISPLAY_END; })
      .sort((a, b) => toMins(a.scheduledTime) - toMins(b.scheduledTime));
  }, [events, incId]);

  if (scheduled.length === 0) {
    return (
      <div className="cop-stack cop-empty-state">
        <IconCalendarEvent size={24} style={{ color: '#ADAFB1' }} />
        <span>No events scheduled today</span>
      </div>
    );
  }

  const HOURS    = Array.from({ length: DISPLAY_END - DISPLAY_START + 1 }, (_, i) => i + DISPLAY_START);
  const nowDate  = new Date();
  const nowMins  = nowDate.getHours() * 60 + nowDate.getMinutes();
  const showNow  = nowMins >= DISPLAY_START * 60 && nowMins <= DISPLAY_END * 60;
  const nowTop   = ((nowMins - DISPLAY_START * 60) / 60) * SLOT_H;

  return (
    <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      <div style={{ position: 'relative', display: 'flex' }}>

        {/* Time gutter */}
        <div style={{ width: TIME_W, flexShrink: 0, borderRight: '1px solid #f1f5f9' }}>
          {HOURS.map(h => (
            <div key={h} style={{
              height: SLOT_H, display: 'flex', alignItems: 'flex-start',
              justifyContent: 'flex-end', paddingRight: 6, paddingTop: 3,
              borderBottom: '1px solid #f1f5f9',
              fontSize: 12, color: '#939598', fontVariantNumeric: 'tabular-nums',
            }}>
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Events area */}
        <div style={{ flex: 1, position: 'relative', height: HOURS.length * SLOT_H }}>
          {/* Hour gridlines */}
          {HOURS.map((_, i) => (
            <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: i * SLOT_H, height: SLOT_H, borderBottom: '1px solid #f1f5f9' }} />
          ))}

          {/* Current time marker */}
          {showNow && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: nowTop, height: 2, background: '#ef4444', zIndex: 10, display: 'flex', alignItems: 'center' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', flexShrink: 0, marginLeft: -3 }} />
            </div>
          )}

          {/* Event blocks */}
          {scheduled.map((evt, idx) => {
            const startMins = toMins(evt.scheduledTime);
            const duration  = Math.max(30, Number(evt.durationMinutes) || 30);
            const top       = ((startMins - DISPLAY_START * 60) / 60) * SLOT_H;
            const height    = Math.max(18, (duration / 60) * SLOT_H - 2);
            const color     = catColors[evt.category] || '#6b7280';
            const done      = evt.status === 'completed';
            const skipped   = evt.status === 'skipped';

            const overlaps = scheduled.slice(0, idx).filter(prev => {
              const ps = toMins(prev.scheduledTime);
              return startMins < ps + (Number(prev.durationMinutes) || 30) && startMins + duration > ps;
            });
            const leftPct  = overlaps.length * 22;
            const widthPct = 100 - leftPct - 2;

            return (
              <div
                key={evt.id}
                title={[evt.title, evt.location, evt.responsible].filter(Boolean).join(' · ')}
                style={{
                  position: 'absolute', top: top + 1,
                  left: `${leftPct}%`, width: `${widthPct}%`, height,
                  background: done ? '#dcfce7' : skipped ? '#f1f5f9' : `${color}18`,
                  borderLeft: `3px solid ${done ? '#16a34a' : skipped ? '#939598' : color}`,
                  borderRadius: '0 4px 4px 0',
                  padding: '2px 5px', overflow: 'hidden', zIndex: 5 + idx,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: done ? '#15803d' : skipped ? '#7B7D7F' : color, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {evt.title}
                </div>
                {height > 24 && (
                  <div style={{ fontSize: 12, color: '#7B7D7F', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {evt.scheduledTime}{evt.location ? ` · ${evt.location}` : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Zone Status (Evacuation / Impact / Staging) ───────────────

function W_EvacZones() {
  const zones = useGeoOpsStore(s => s.zones);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';

  const relevant = useMemo(() => {
    const iZones = incId ? zones.filter(z => String(z.incidentId) === incId) : zones;
    return iZones.filter(z => z.status === 'active');
  }, [zones, incId]);

  const byType: Record<string, typeof relevant> = {};
  relevant.forEach(z => {
    if (!byType[z.zoneType]) byType[z.zoneType] = [];
    byType[z.zoneType].push(z);
  });

  const typeConfig: Record<string, { label: string; color: string; emoji: string }> = {
    evacuation_zone: { label: 'Evacuation Zones', color: '#d97706', emoji: '⚠' },
    impact_zone:     { label: 'Impact Zones',     color: '#D33423', emoji: '🔴' },
    staging_area:    { label: 'Staging Areas',    color: '#0284c7', emoji: '📍' },
    exclusion_zone:  { label: 'Exclusion Zones',  color: '#7c3aed', emoji: '🚫' },
    custom:          { label: 'Custom Zones',     color: '#6b7280', emoji: '📌' },
  };

  if (relevant.length === 0) {
    return (
      <div className="cop-stack cop-empty-state">
        <IconArrowsMove size={24} style={{ color: '#ADAFB1' }} />
        <span>No active zones defined</span>
      </div>
    );
  }

  const order = ['evacuation_zone', 'impact_zone', 'exclusion_zone', 'staging_area', 'custom'];

  return (
    <div className="cop-stack">
      {order.filter(t => byType[t]?.length).map(type => {
        const cfg = typeConfig[type];
        const list = byType[type];
        return (
          <div key={type}>
            <div className="cop-zone-type-header">
              <span className="cop-zone-dot" style={{ background: cfg.color }} />
              <span className="cop-zone-type-label" style={{ color: cfg.color }}>{cfg.label}</span>
              <span className="cop-zone-count">{list.length}</span>
            </div>
            <div className="cop-zone-list">
              {list.slice(0, 4).map(z => (
                <div key={z.id} className="cop-zone-item">
                  <span className="cop-zone-name">{z.name}</span>
                </div>
              ))}
              {list.length > 4 && <div className="cop-sub">+{list.length - 4} more</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ICS Command Structure ─────────────────────────────────────

function W_ICSCommand() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const checkins = useRollCallStore(s => s.checkins);

  if (!activeIncident) {
    return (
      <div className="cop-stack cop-empty-state">
        <IconHierarchy size={24} style={{ color: '#ADAFB1' }} />
        <span>No active incident</span>
      </div>
    );
  }

  // Personnel currently checked in
  const inEOC = checkins.filter(c => c.checkInTime && !c.checkOutTime).length;

  const icsPositions = [
    { role: 'Incident Commander', name: activeIncident.commander || '—', section: 'command', color: '#4B3FFF' },
    { role: 'Location', name: activeIncident.location || '—', section: 'command', color: '#6b7280' },
  ];

  const sections = [
    { label: 'Operations', color: '#D33423', icon: '⚙' },
    { label: 'Planning',   color: '#4B3FFF', icon: '📋' },
    { label: 'Logistics',  color: '#0284c7', icon: '📦' },
    { label: 'Finance',    color: '#037730', icon: '💰' },
  ];

  return (
    <div className="cop-stack">
      {icsPositions.map(p => (
        <div key={p.role} className="cop-ics-row">
          <span className="cop-ics-role">{p.role}</span>
          <span className="cop-ics-name" style={{ color: p.color }}>{p.name}</span>
        </div>
      ))}
      <div style={{ borderTop: '1px solid #F2F2F2', paddingTop: 8, marginTop: 4 }}>
        <div className="cop-section-label">General Staff</div>
        <div className="cop-ics-sections">
          {sections.map(s => (
            <div key={s.label} className="cop-ics-section-chip" style={{ borderColor: `${s.color}30` }}>
              <span className="cop-ics-section-icon">{s.icon}</span>
              <span className="cop-ics-section-label" style={{ color: s.color }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ borderTop: '1px solid #F2F2F2', paddingTop: 8, marginTop: 4 }}>
        <div className="cop-data-row">
          <span className="cop-data-row-label"><span className="cop-data-row-pip" style={{ background: '#037730' }} />EOC Personnel In</span>
          <span className="cop-data-row-val" style={{ color: '#037730' }}>{inEOC}</span>
        </div>
        <div className="cop-data-row">
          <span className="cop-data-row-label">Elapsed</span>
          <span className="cop-data-row-val" style={{ fontSize: 12 }}>
            {computeElapsed(activeIncident.startDate) || '—'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Resource / Personnel Fill Rate ────────────────────────────

function W_ResourceStatus() {
  const volunteers = useVolunteerStore(s => s.volunteers);
  const assignments = useVolunteerStore(s => s.assignments);
  const checkins = useRollCallStore(s => s.checkins);
  const events = useRollCallStore(s => s.events);

  const activeVols = volunteers.filter(v => v.status === 'active').length;
  const totalVols = volunteers.length;
  const deployed = assignments.filter(a => a.status === 'confirmed' || a.status === 'scheduled').length;
  const completedAssignments = assignments.filter(a => a.status === 'completed').length;

  const openRCEvents = events.filter(e => e.eventStatus === 'open').length;
  const inEOC = checkins.filter(c => c.checkInTime && !c.checkOutTime).length;

  const fillPct = totalVols > 0 ? Math.round((activeVols / totalVols) * 100) : 0;
  const fillColor = fillPct >= 75 ? '#037730' : fillPct >= 40 ? '#d97706' : '#D33423';

  return (
    <div className="cop-stack">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <div>
          <span className="cop-num" style={{ color: fillColor }}>{fillPct}%</span>
          <div className="cop-sub">volunteer fill rate</div>
        </div>
        <svg width={52} height={52} viewBox="0 0 52 52" style={{ flexShrink: 0 }}>
          <circle cx={26} cy={26} r={20} fill="none" stroke="#DDDEDE" strokeWidth={6} />
          <circle cx={26} cy={26} r={20} fill="none"
            stroke={fillColor} strokeWidth={6}
            strokeDasharray={`${(fillPct / 100) * (2 * Math.PI * 20)} ${2 * Math.PI * 20}`}
            strokeLinecap="round" transform="rotate(-90 26 26)"
          />
          <text x={26} y={27} textAnchor="middle" dominantBaseline="middle"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700, fill: '#323334' }}>
            {fillPct}%
          </text>
        </svg>
      </div>
      <div className="cop-data-rows">
        {[
          { label: 'Active Volunteers',  value: activeVols,           color: '#037730' },
          { label: 'Deployed',           value: deployed,             color: '#3b82f6' },
          { label: 'Assignments Done',   value: completedAssignments, color: '#0d9488' },
          { label: 'EOC Personnel In',   value: inEOC,                color: '#4B3FFF' },
          { label: 'Open RC Events',     value: openRCEvents,         color: '#d97706' },
        ].map(r => (
          <div key={r.label} className="cop-data-row">
            <span className="cop-data-row-label">
              <span className="cop-data-row-pip" style={{ background: r.color }} />{r.label}
            </span>
            <span className="cop-data-row-val">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Situation Map ─────────────────────────────────────────────

function W_MapOverview() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const shelters = useShelterStore(s => s.shelters);
  const zones = useGeoOpsStore(s => s.zones);

  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const lat = activeIncident?.latitude ? parseFloat(activeIncident.latitude) : null;
  const lon = activeIncident?.longitude ? parseFloat(activeIncident.longitude) : null;

  const iShelters = incId ? shelters.filter(s => s.incidentId === incId) : shelters;
  const iZones = (incId ? zones.filter(z => String(z.incidentId) === incId) : zones).filter(z => z.status === 'active');

  const center: [number, number] = lat && lon ? [lat, lon] : [38.5, -98]; // center of USA fallback

  if (!lat || !lon) {
    return (
      <div className="cop-stack cop-empty-state">
        <IconMap size={32} style={{ color: '#ADAFB1' }} />
        <span>No coordinates on active incident — add lat/lon in Incidents module</span>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
      <MapContainer
        center={center} zoom={11}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
        />

        {/* Incident location */}
        <CircleMarker center={center} radius={10} pathOptions={{ color: '#4B3FFF', fillColor: '#4B3FFF', fillOpacity: 0.9 }}>
          <Popup><strong>{activeIncident?.name}</strong><br />Incident location</Popup>
        </CircleMarker>

        {/* Shelters */}
        {iShelters.filter(s => s.latitude && s.longitude).map(s => {
          const pct = s.capacity > 0 ? s.currentPopulation / s.capacity : 0;
          const color = pct > 0.9 ? '#D33423' : pct > 0.7 ? '#d97706' : '#037730';
          return (
            <CircleMarker
              key={s.id}
              center={[parseFloat(s.latitude), parseFloat(s.longitude)]}
              radius={7} pathOptions={{ color, fillColor: color, fillOpacity: 0.8 }}
            >
              <Popup>
                <strong>{s.name}</strong><br />
                {s.currentPopulation}/{s.capacity} · {s.status}
              </Popup>
            </CircleMarker>
          );
        })}

        {/* GeoOps zones */}
        {iZones.filter(z => z.geometry).map(z => (
          <GeoJSON
            key={z.id}
            data={z.geometry as any}
            style={{ color: z.color || '#d97706', fillColor: z.color || '#d97706', fillOpacity: z.opacity ?? 0.15, weight: 2 }}
          >
            {/* Leaflet GeoJSON doesn't use Popup like this — add onEachFeature if needed */}
          </GeoJSON>
        ))}
      </MapContainer>

      {/* Map legend */}
      <div className="cop-map-legend">
        <div className="cop-map-legend-item"><span className="cop-map-legend-dot" style={{ background: '#4B3FFF' }} />Incident</div>
        <div className="cop-map-legend-item"><span className="cop-map-legend-dot" style={{ background: '#037730' }} />Shelter</div>
        {iZones.length > 0 && <div className="cop-map-legend-item"><span className="cop-map-legend-dot" style={{ background: '#d97706' }} />Zones</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ── MODULE SUMMARY WIDGETS (unchanged) ───────────────────────
// ══════════════════════════════════════════════════════════════

function W_IncidentStats() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const shelters = useShelterStore(s => s.shelters);
  const projects = usePAGrantsStore(s => s.projects);
  const assessments = useImpactStore(s => s.assessments);
  const loadTickets = useDebrisOpsStore(s => s.loadTickets);
  const volunteers = useVolunteerStore(s => s.volunteers);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const stats = [
    { label: 'Open Shelters', value: (incId ? shelters.filter(s => s.incidentId === incId) : shelters).filter(s => s.status === 'open').length, color: '#0284c7' },
    { label: 'PA Projects', value: projects.filter(p => p.workType !== 'hmgp').length, color: '#6366f1' },
    { label: 'Assessments', value: (incId ? assessments.filter(a => a.incidentId === incId) : assessments).length, color: '#B12525' },
    { label: 'Debris Tickets', value: (incId ? loadTickets.filter(t => t.incidentId === incId) : loadTickets).length, color: '#d97706' },
    { label: 'Volunteers', value: volunteers.filter(v => v.status === 'active').length, color: '#7c3aed' },
  ];
  return (
    <div className="cop-stat-chips">
      {stats.map(s => (
        <div key={s.label} className="cop-stat-chip" style={{ borderColor: `${s.color}30` }}>
          <span className="cop-stat-chip-num" style={{ color: s.color }}>{s.value}</span>
          <span className="cop-stat-chip-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function W_Shelter({ mode = 'stat' }: { mode?: ChartMode }) {
  const shelters = useShelterStore(s => s.shelters);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const iSh = incId ? shelters.filter(s => s.incidentId === incId) : shelters;
  const totalCap = iSh.reduce((s, sh) => s + sh.capacity, 0);
  const totalPop = iSh.reduce((s, sh) => s + sh.currentPopulation, 0);
  const pct = totalCap > 0 ? Math.round((totalPop / totalCap) * 100) : 0;

  if (mode === 'bar') {
    const chartData = iSh.map(s => ({
      name: s.name?.length > 12 ? s.name.slice(0, 12) + '…' : (s.name || 'Unnamed'),
      Population: s.currentPopulation, Capacity: s.capacity,
    }));
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#0284c7' }}>{iSh.filter(s => s.status === 'open').length}</span>
          <span className="cop-chart-num-label">open</span>
        </div>
        <ResponsiveContainer width="100%" height="80%">
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            {CHART_GRID}
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#939598' }} />
            <YAxis tick={{ fontSize: 12, fill: '#939598' }} />
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
            <Bar dataKey="Population" fill="#0284c7" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Capacity" fill="#e0f2fe" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cop-stack">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <div>
          <span className="cop-num" style={{ color: '#0284c7' }}>{iSh.filter(s => s.status === 'open').length}</span>
          <div className="cop-sub">shelters open</div>
          <div className="cop-sub" style={{ marginTop: 2 }}>{totalPop.toLocaleString()} / {totalCap.toLocaleString()} capacity</div>
        </div>
        <svg width={52} height={52} viewBox="0 0 52 52" style={{ flexShrink: 0 }}>
          <circle cx={26} cy={26} r={20} fill="none" stroke="#DDDEDE" strokeWidth={6} />
          <circle cx={26} cy={26} r={20} fill="none"
            stroke={pct > 80 ? '#D33423' : pct > 50 ? '#d97706' : '#037730'}
            strokeWidth={6}
            strokeDasharray={`${(pct / 100) * (2 * Math.PI * 20)} ${2 * Math.PI * 20}`}
            strokeLinecap="round" transform="rotate(-90 26 26)"
          />
          <text x={26} y={27} textAnchor="middle" dominantBaseline="middle"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700, fill: '#323334' }}>
            {pct}%
          </text>
        </svg>
      </div>
      <div className="cop-shelter-rows">
        {iSh.map(s => {
          const sp = s.capacity > 0 ? Math.round((s.currentPopulation / s.capacity) * 100) : 0;
          const c = sp >= 90 ? '#D33423' : sp >= 70 ? '#d97706' : '#037730';
          return (
            <div key={s.id}>
              <div className="cop-shelter-row-meta">
                <span className="cop-shelter-row-name">{s.name}</span>
                <span className="cop-shelter-row-cap">{s.currentPopulation}/{s.capacity}</span>
              </div>
              <div className="cop-bar-track" style={{ marginTop: 2 }}>
                <div className="cop-bar-fill" style={{ width: `${sp}%`, background: c }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function W_IntakeTriage({ mode = 'stat' }: { mode?: ChartMode }) {
  const individuals = useIntakeStore(s => s.individuals);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const filtered = incId ? individuals.filter(i => i.incidentId === incId) : individuals;
  const active = filtered.filter(i => i.status === 'active');
  const r = active.filter(i => i.triage === 'var(--color-error)').length;
  const y = active.filter(i => i.triage === '#C68700').length;
  const g = active.filter(i => i.triage === 'var(--color-success)').length;
  const triageData = [
    { name: 'Red', value: r, color: '#D33423' },
    { name: 'Yellow', value: y, color: '#d97706' },
    { name: 'Green', value: g, color: '#037730' },
  ];
  const total = r + y + g;

  if (mode === 'donut') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#b91c1c' }}>{active.length}</span>
          <span className="cop-chart-num-label">active</span>
        </div>
        <ResponsiveContainer width="100%" height="75%">
          <PieChart>
            <Pie data={triageData.filter(d => d.value > 0)} dataKey="value" nameKey="name"
              innerRadius={45} outerRadius={70} paddingAngle={2}>
              {triageData.filter(d => d.value > 0).map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="cop-donut-center-label">{total} total</div>
      </div>
    );
  }

  return (
    <div className="cop-stack" style={{ justifyContent: 'center' }}>
      <div>
        <span className="cop-num" style={{ color: '#b91c1c' }}>{active.length}</span>
        <span className="cop-num-label">active</span>
      </div>
      <div className="cop-triage-chips">
        <div className="cop-triage-chip" style={{ background: '#FCF7F7', borderColor: 'rgba(211,52,35,0.25)' }}>
          <span className="cop-triage-chip-num" style={{ color: '#D33423' }}>{r}</span>
          <span className="cop-triage-chip-label" style={{ color: '#D33423' }}>Red</span>
        </div>
        <div className="cop-triage-chip" style={{ background: '#FDF7F4', borderColor: 'rgba(136,86,4,0.25)' }}>
          <span className="cop-triage-chip-num" style={{ color: '#885604' }}>{y}</span>
          <span className="cop-triage-chip-label" style={{ color: '#885604' }}>Yellow</span>
        </div>
        <div className="cop-triage-chip" style={{ background: '#EFFDF1', borderColor: 'rgba(3,119,48,0.25)' }}>
          <span className="cop-triage-chip-num" style={{ color: '#037730' }}>{g}</span>
          <span className="cop-triage-chip-label" style={{ color: '#037730' }}>Green</span>
        </div>
      </div>
      <div className="cop-sub">{filtered.length} total · {filtered.filter(i => i.status === 'discharged').length} discharged</div>
    </div>
  );
}

function W_Business({ mode = 'stat' }: { mode?: ChartMode }) {
  const organizations = useMainStreetStore(s => s.organizations);
  const statuses: Record<string, number> = {};
  organizations.forEach(o => { statuses[o.opStatus || 'unknown'] = (statuses[o.opStatus || 'unknown'] || 0) + 1; });
  const colors: Record<string, string> = {
    open: '#037730', closed: '#D33423', damaged: '#d97706',
    limited: '#885604', pending: '#4B3FFF', unknown: '#939598',
  };
  const data = Object.entries(statuses).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({
    name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value: v,
    color: colors[k] || '#939598',
  }));

  if (mode === 'donut') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#d97706' }}>{organizations.length}</span>
          <span className="cop-chart-num-label">businesses</span>
        </div>
        <ResponsiveContainer width="100%" height="75%">
          <PieChart>
            <Pie data={data.filter(d => d.value > 0)} dataKey="value" nameKey="name"
              innerRadius={45} outerRadius={70} paddingAngle={2}>
              {data.filter(d => d.value > 0).map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (mode === 'bar') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#d97706' }}>{organizations.length}</span>
          <span className="cop-chart-num-label">businesses</span>
        </div>
        <ResponsiveContainer width="100%" height="80%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            {CHART_GRID}
            <XAxis type="number" tick={{ fontSize: 12, fill: '#939598' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#939598' }} width={60} />
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cop-stack">
      <div style={{ flexShrink: 0 }}>
        <span className="cop-num" style={{ color: '#d97706' }}>{organizations.length}</span>
        <span className="cop-num-label">businesses</span>
      </div>
      <div className="cop-data-rows">
        {data.map(d => (
          <div key={d.name} className="cop-data-row">
            <span className="cop-data-row-label">
              <span className="cop-data-row-pip" style={{ background: d.color }} />{d.name}
            </span>
            <span className="cop-data-row-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function W_PAPipeline({ mode = 'stat' }: { mode?: ChartMode }) {
  const projects = usePAGrantsStore(s => s.projects);
  const pa = projects.filter(p => p.workType !== 'hmgp');
  const phases: Record<string, number> = {};
  pa.forEach(p => { phases[p.phase] = (phases[p.phase] || 0) + 1; });
  const labels: Record<string, string> = {
    formulation: 'Formulation', review: 'Review',
    obligation: 'Obligation', work_in_progress: 'WIP', closeout: 'Closeout',
  };
  const phaseColors: Record<string, string> = {
    formulation: '#3b82f6', review: '#d97706',
    obligation: '#037730', work_in_progress: '#d97706', closeout: '#4B3FFF',
  };
  const data = Object.entries(labels).map(([k, l]) => ({
    name: l, value: phases[k] || 0, color: phaseColors[k] || '#939598',
  }));

  if (mode === 'donut') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#1E40AF' }}>{pa.length}</span>
          <span className="cop-chart-num-label">projects</span>
        </div>
        <ResponsiveContainer width="100%" height="75%">
          <PieChart>
            <Pie data={data.filter(d => d.value > 0)} dataKey="value" nameKey="name"
              innerRadius={45} outerRadius={70} paddingAngle={2}>
              {data.filter(d => d.value > 0).map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }
  if (mode === 'bar') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#1E40AF' }}>{pa.length}</span>
          <span className="cop-chart-num-label">projects</span>
        </div>
        <ResponsiveContainer width="100%" height="80%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            {CHART_GRID}
            <XAxis type="number" tick={{ fontSize: 12, fill: '#939598' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#939598' }} width={70} />
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cop-stack">
      <div style={{ flexShrink: 0 }}>
        <span className="cop-num" style={{ color: '#1E40AF' }}>{pa.length}</span>
        <span className="cop-num-label">projects</span>
      </div>
      <div className="cop-data-rows">
        {data.map(d => (
          <div key={d.name} className="cop-data-row">
            <span className="cop-data-row-label">
              <span className="cop-data-row-pip" style={{ background: d.color }} />{d.name}
            </span>
            <span className="cop-data-row-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function W_PAFinancial({ mode = 'stat' }: { mode?: ChartMode }) {
  const projects = usePAGrantsStore(s => s.projects);
  const pa = projects.filter(p => p.workType !== 'hmgp');
  const est = pa.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
  const obl = pa.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);
  const drawn = pa.reduce((s, p) => s + (parseFloat(p.drawdownAmount) || 0), 0);
  const barData = [
    { name: 'Estimated', value: est, color: '#939598' },
    { name: 'Obligated', value: obl, color: '#037730' },
    { name: 'Drawn Down', value: drawn, color: '#4B3FFF' },
  ];

  if (mode === 'bar') {
    return (
      <div className="cop-chart-stack" style={{ height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={barData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            {CHART_GRID}
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#939598' }} />
            <YAxis tick={{ fontSize: 12, fill: '#939598' }} tickFormatter={(v) => fmtMoney(v)} width={48} />
            <RTooltip
              formatter={(v: number) => [fmtMoney(v)]}
              contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {barData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cop-stack" style={{ justifyContent: 'center' }}>
      {barData.map(i => (
        <div key={i.name} className="cop-financial-row">
          <div className="cop-financial-meta">
            <span className="cop-financial-label">{i.name}</span>
            <span className="cop-financial-value" style={{ color: i.color }}>{fmtMoney(i.value)}</span>
          </div>
          {est > 0 && (
            <div className="cop-bar-track">
              <div className="cop-bar-fill" style={{ width: `${(i.value / est) * 100}%`, background: i.color }} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function W_PARFR({ mode = 'stat' }: { mode?: ChartMode }) {
  const rfrs = usePAGrantsStore(s => s.reimbursementRequests);
  const totReq = rfrs.reduce((s, r) => s + (parseFloat(r.requestedAmount) || 0), 0);
  const totAppr = rfrs.reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0);
  const statuses: Record<string, number> = {};
  rfrs.forEach(r => { statuses[r.status] = (statuses[r.status] || 0) + 1; });
  const colors: Record<string, string> = {
    draft: '#939598', submitted: '#3b82f6', under_review: '#4B3FFF',
    approved: '#037730', paid: '#0d9488', denied: '#D33423', revision_needed: '#d97706',
  };
  const data = Object.entries(statuses).map(([k, v]) => ({
    name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: v, color: colors[k] || '#939598',
  }));

  if (mode === 'donut') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#0e7490' }}>{rfrs.length}</span>
          <span className="cop-chart-num-label">RFRs</span>
        </div>
        <ResponsiveContainer width="100%" height="75%">
          <PieChart>
            <Pie data={data.filter(d => d.value > 0)} dataKey="value" nameKey="name"
              innerRadius={45} outerRadius={70} paddingAngle={2}>
              {data.filter(d => d.value > 0).map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cop-stack">
      <div style={{ flexShrink: 0 }}>
        <span className="cop-num" style={{ color: '#0e7490' }}>{rfrs.length}</span>
        <span className="cop-num-label">RFRs</span>
      </div>
      <div className="cop-financial-row">
        <div className="cop-financial-meta">
          <span className="cop-financial-label">Requested</span>
          <span className="cop-financial-value" style={{ color: '#939598' }}>{fmtMoney(totReq)}</span>
        </div>
      </div>
      <div className="cop-financial-row">
        <div className="cop-financial-meta">
          <span className="cop-financial-label">Approved</span>
          <span className="cop-financial-value" style={{ color: '#037730' }}>{fmtMoney(totAppr)}</span>
        </div>
      </div>
      <div className="cop-data-rows">
        {data.map(d => (
          <div key={d.name} className="cop-data-row">
            <span className="cop-data-row-label">
              <span className="cop-data-row-pip" style={{ background: d.color }} />{d.name}
            </span>
            <span className="cop-data-row-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function W_Debris({ mode = 'stat' }: { mode?: ChartMode }) {
  const tix = useDebrisOpsStore(s => s.loadTickets);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const tickets = incId ? tix.filter(t => t.incidentId === incId) : tix;
  const verified = tickets.filter(t => t.status === 'verified' || t.status === 'delivered').length;
  const pending = tickets.filter(t => t.status === 'draft' || t.status === 'in_transit' || t.status === 'at_site').length;
  const totalCY = tickets.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0);
  const verifiedPct = tickets.length > 0 ? Math.round((verified / tickets.length) * 100) : 0;
  const byType: Record<string, number> = {};
  tickets.forEach(t => { byType[t.debrisType || 'unknown'] = (byType[t.debrisType || 'unknown'] || 0) + 1; });
  const typeColors: Record<string, string> = {
    vegetative: '#037730', c_and_d: '#d97706', mixed: '#4B3FFF',
    hazardous: '#D33423', unknown: '#939598',
  };
  const typeData = Object.entries(byType).map(([k, v]) => ({
    name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: v, color: typeColors[k] || '#939598', fill: typeColors[k] || '#939598',
  }));

  if (mode === 'donut') {
    const radialData = [{ name: 'Verified', value: verifiedPct, fill: '#d97706' }];
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#d97706' }}>{tickets.length}</span>
          <span className="cop-chart-num-label">tickets</span>
        </div>
        <ResponsiveContainer width="100%" height="70%">
          <RadialBarChart innerRadius="60%" outerRadius="90%"
            data={radialData} startAngle={180} endAngle={0}>
            <RadialBar dataKey="value" cornerRadius={4} background={{ fill: '#F2F2F2' }} />
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="cop-sub" style={{ textAlign: 'center' }}>
          {verifiedPct}% verified · {totalCY.toLocaleString(undefined, { maximumFractionDigits: 0 })} CY
        </div>
      </div>
    );
  }

  return (
    <div className="cop-stack">
      <div style={{ flexShrink: 0 }}>
        <span className="cop-num" style={{ color: '#d97706' }}>{tickets.length}</span>
        <span className="cop-num-label">tickets</span>
      </div>
      <div className="cop-mini-chips" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="cop-mini-chip" style={{ borderColor: 'rgba(3,119,48,0.25)' }}>
          <span className="cop-mini-chip-num" style={{ color: '#037730' }}>{verified}</span>
          <span className="cop-mini-chip-label">Verified</span>
        </div>
        <div className="cop-mini-chip" style={{ borderColor: 'rgba(217,119,6,0.25)' }}>
          <span className="cop-mini-chip-num" style={{ color: '#d97706' }}>{pending}</span>
          <span className="cop-mini-chip-label">Pending</span>
        </div>
      </div>
      <div>
        <div className="cop-sub">Total CY</div>
        <span style={{ fontFamily: "'DM Mono', monospace", fontVariantNumeric: 'tabular-nums', fontSize: 20, fontWeight: 700, color: '#d97706' }}>
          {totalCY.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>
      {typeData.length > 0 && (
        <div className="cop-data-rows">
          {typeData.map(d => (
            <div key={d.name} className="cop-data-row">
              <span className="cop-data-row-label">
                <span className="cop-data-row-pip" style={{ background: d.color }} />{d.name}
              </span>
              <span className="cop-data-row-val">{d.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function W_Impact({ mode = 'stat' }: { mode?: ChartMode }) {
  const assessments = useImpactStore(s => s.assessments);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const ia = incId ? assessments.filter(a => a.incidentId === incId) : assessments;
  const byCat: Record<string, number> = {};
  ia.forEach(a => { byCat[a.damageCategory || 'unknown'] = (byCat[a.damageCategory || 'unknown'] || 0) + 1; });
  const catColors: Record<string, string> = {
    destroyed: '#D33423', major: '#d97706', minor: '#885604',
    affected: '#3b82f6', inaccessible: '#939598', unknown: '#ADAFB1',
  };
  const data = Object.entries(byCat).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({
    name: k.replace(/\b\w/g, c => c.toUpperCase()), value: v, color: catColors[k] || '#939598',
  }));

  if (mode === 'donut') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#D33423' }}>{ia.length}</span>
          <span className="cop-chart-num-label">assessments</span>
        </div>
        <ResponsiveContainer width="100%" height="75%">
          <PieChart>
            <Pie data={data.filter(d => d.value > 0)} dataKey="value" nameKey="name"
              innerRadius={45} outerRadius={70} paddingAngle={2}>
              {data.filter(d => d.value > 0).map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }
  if (mode === 'bar') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#D33423' }}>{ia.length}</span>
          <span className="cop-chart-num-label">assessments</span>
        </div>
        <ResponsiveContainer width="100%" height="80%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            {CHART_GRID}
            <XAxis type="number" tick={{ fontSize: 12, fill: '#939598' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#939598' }} width={70} />
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cop-stack">
      <div style={{ flexShrink: 0 }}>
        <span className="cop-num" style={{ color: '#D33423' }}>{ia.length}</span>
        <span className="cop-num-label">assessments</span>
      </div>
      <div className="cop-data-rows">
        {data.map(d => (
          <div key={d.name} className="cop-data-row">
            <span className="cop-data-row-label">
              <span className="cop-data-row-pip" style={{ background: d.color }} />{d.name}
            </span>
            <span className="cop-data-row-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function W_Volunteers({ mode = 'stat' }: { mode?: ChartMode }) {
  const volunteers = useVolunteerStore(s => s.volunteers);
  const assignments = useVolunteerStore(s => s.assignments);
  const active = volunteers.filter(v => v.status === 'active').length;
  const deployed = assignments.filter(a => a.status === 'confirmed' || a.status === 'scheduled').length;
  const completed = assignments.filter(a => a.status === 'completed').length;
  const inactive = volunteers.length - active;
  const volData = [
    { name: 'Active', value: active, color: '#037730' },
    { name: 'Deployed', value: deployed, color: '#3b82f6' },
    { name: 'Completed', value: completed, color: '#0d9488' },
    { name: 'Inactive', value: inactive, color: '#939598' },
  ];

  if (mode === 'donut') {
    return (
      <div className="cop-chart-stack">
        <div className="cop-chart-header-stat">
          <span className="cop-chart-num" style={{ color: '#7c3aed' }}>{volunteers.length}</span>
          <span className="cop-chart-num-label">volunteers</span>
        </div>
        <ResponsiveContainer width="100%" height="75%">
          <PieChart>
            <Pie data={volData.filter(d => d.value > 0)} dataKey="value" nameKey="name"
              innerRadius={45} outerRadius={70} paddingAngle={2}>
              {volData.filter(d => d.value > 0).map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <RTooltip contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #DDDEDE' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cop-stack" style={{ justifyContent: 'center' }}>
      <div>
        <span className="cop-num" style={{ color: '#7c3aed' }}>{volunteers.length}</span>
        <span className="cop-num-label">total</span>
      </div>
      <div className="cop-mini-chips" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="cop-mini-chip" style={{ borderColor: 'rgba(3,119,48,0.25)' }}>
          <span className="cop-mini-chip-num" style={{ color: '#037730' }}>{active}</span>
          <span className="cop-mini-chip-label">Active</span>
        </div>
        <div className="cop-mini-chip" style={{ borderColor: 'rgba(59,130,246,0.25)' }}>
          <span className="cop-mini-chip-num" style={{ color: '#3b82f6' }}>{deployed}</span>
          <span className="cop-mini-chip-label">Deployed</span>
        </div>
        <div className="cop-mini-chip" style={{ borderColor: 'rgba(13,148,136,0.25)' }}>
          <span className="cop-mini-chip-num" style={{ color: '#0d9488' }}>{completed}</span>
          <span className="cop-mini-chip-label">Done</span>
        </div>
      </div>
    </div>
  );
}

function W_RollCall() {
  const events = useRollCallStore(s => s.events);
  const checkins = useRollCallStore(s => s.checkins);
  const openEvents = events.filter(e => e.eventStatus === 'open').length;
  const currentlyIn = checkins.filter(c => c.checkInTime && !c.checkOutTime).length;
  return (
    <div className="cop-stack" style={{ justifyContent: 'center' }}>
      <div className="cop-mini-chips" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="cop-mini-chip" style={{ borderColor: 'rgba(59,130,246,0.25)' }}>
          <span className="cop-mini-chip-num" style={{ color: '#3b82f6' }}>{openEvents}</span>
          <span className="cop-mini-chip-label">Open Events</span>
        </div>
        <div className="cop-mini-chip" style={{ borderColor: 'rgba(3,119,48,0.25)' }}>
          <span className="cop-mini-chip-num" style={{ color: '#037730' }}>{currentlyIn}</span>
          <span className="cop-mini-chip-label">Checked In</span>
        </div>
      </div>
      <div className="cop-sub" style={{ textAlign: 'center' }}>{checkins.length} total check-ins</div>
    </div>
  );
}

function W_Activity() {
  const notes = useHandoffStore(s => s.notes);
  const sorted = useMemo(
    () => [...notes].sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || '')).slice(0, 10),
    [notes],
  );
  return (
    <div className="cop-activity-list">
      {sorted.length === 0 && <div className="cop-activity-empty">No recent activity</div>}
      {sorted.map(n => (
        <div key={n.id} className="cop-activity-item">
          <div className="cop-activity-title">{n.positionName || 'Handoff'} — {n.fromShift} to {n.toShift}</div>
          <div className="cop-activity-sub">{n.senderName || '—'} · {fmtDateTime(n.datecreated)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Shared exports (used by Dashboard home page + Settings) ──

export const HOME_WIDGET_KEY = 'home-widget-ids-v2';
export const HOME_WIDGET_DEFAULT = ['readiness', 'schedule', 'pending'];
export type { WidgetDef, ChartMode };
export { WIDGETS };

const WIDGET_MAP: Record<string, React.ComponentType<{ mode?: ChartMode }>> = {
  life_safety:     W_LifeSafety,
  op_period:       W_OpPeriod,
  weather:         W_Weather,
  map_overview:    W_MapOverview,
  evac_zones:      W_EvacZones,
  ics_command:     W_ICSCommand,
  battle_rhythm:   W_BattleRhythm,
  resource_status: W_ResourceStatus,
  incident_stats:  W_IncidentStats,
  shelter_occupancy: W_Shelter,
  intake_triage:   W_IntakeTriage,
  business_status: W_Business,
  pa_pipeline:     W_PAPipeline,
  pa_financial:    W_PAFinancial,
  pa_rfr:          W_PARFR,
  debris_progress: W_Debris,
  impact_summary:  W_Impact,
  volunteers:      W_Volunteers,
  rollcall:        W_RollCall,
  activity_feed:   W_Activity,
};

export { WIDGET_MAP };

// ── Main COP ─────────────────────────────────────────────────

export default function COP() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const [activeWidgets, setActiveWidgets] = useState<string[]>(() => loadJson(LS_WIDGETS, DEFAULT_WIDGETS));
  const [isEditing, setIsEditing] = useState(false);
  const [widgetModes, setWidgetModes] = useState<Record<string, ChartMode>>(() => loadJson(LS_MODES, {}));

  const setMode = (id: string, mode: ChartMode) => {
    const next = { ...widgetModes, [id]: mode };
    setWidgetModes(next); save(LS_MODES, next);
  };

  const loadAllData = useCallback(() => {
    useShelterStore.getState().loadShelters();
    usePAGrantsStore.getState().fetchProjects();
    usePAGrantsStore.getState().fetchOrganizations();
    usePAGrantsStore.getState().fetchReimbursements();
    useImpactStore.getState().loadAssessments();
    useDebrisOpsStore.getState().loadLoadTickets();
    useVolunteerStore.getState().loadVolunteers();
    useVolunteerStore.getState().loadAssignments();
    useHandoffStore.getState().loadNotes();
    useRollCallStore.getState().loadAll();
    useMainStreetStore.getState().loadOrganizations();
    useGeoOpsStore.getState().fetchZones();
    if (activeIncident?.id) {
      const incId = String(activeIncident.id);
      useIntakeStore.getState().loadIndividuals(incId);
      useBattleRhythmStore.getState().loadActivations(incId);
      useBattleRhythmStore.getState().loadEvents(incId);
    }
  }, [activeIncident?.id]);

  useEffect(() => { loadAllData(); }, [loadAllData]);
  usePolling(loadAllData, 30000);

  const defaultLayout = useMemo((): LayoutItem[] => {
    let x = 0, y = 0;
    return activeWidgets.map(id => {
      const def = WIDGETS.find(w => w.id === id);
      const w = def?.defaultW || 4, h = def?.defaultH || 3;
      if (x + w > 12) { x = 0; y += h; }
      const item: LayoutItem = { i: id, x, y, w, h, minW: 3, minH: 2 };
      x += w; return item;
    });
  }, [activeWidgets]);

  const [layouts, setLayouts] = useState<LayoutItem[]>(() => loadJson(LS_KEY, null) || defaultLayout);

  useEffect(() => {
    const ids = new Set(layouts.map(l => l.i));
    const added = activeWidgets.filter(id => !ids.has(id));
    if (added.length > 0) {
      const maxY = layouts.reduce((m, l) => Math.max(m, l.y + l.h), 0);
      let x = 0;
      const items = added.map(id => {
        const def = WIDGETS.find(w => w.id === id);
        const w = def?.defaultW || 4, h = def?.defaultH || 3;
        if (x + w > 12) x = 0;
        const item: LayoutItem = { i: id, x, y: maxY, w, h, minW: 3, minH: 2 };
        x += w; return item;
      });
      setLayouts(prev => [...prev.filter(l => activeWidgets.includes(l.i)), ...items]);
    } else {
      setLayouts(prev => prev.filter(l => activeWidgets.includes(l.i)));
    }
  }, [activeWidgets]);

  const addWidget = (id: string) => { const next = [...activeWidgets, id]; setActiveWidgets(next); save(LS_WIDGETS, next); };
  const removeWidget = (id: string) => { const next = activeWidgets.filter(w => w !== id); setActiveWidgets(next); save(LS_WIDGETS, next); };
  const resetLayout = () => {
    setActiveWidgets(DEFAULT_WIDGETS); save(LS_WIDGETS, DEFAULT_WIDGETS);
    localStorage.removeItem(LS_KEY); localStorage.removeItem(LS_MODES);
    setLayouts(defaultLayout); setWidgetModes({});
    notifications.show({ message: 'Layout reset', color: 'var(--color-primary-700)' });
  };

  const available = WIDGETS.filter(w => !activeWidgets.includes(w.id));

  return (
    <div className="cop-shell">
      <IncidentBanner />
      <KpiStrip />

      {isEditing && (
        <div className="cop-edit-banner">
          Drag widgets to rearrange · Resize from corners · Click the trash icon to remove
        </div>
      )}

      <div className="cop-toolbar">
        <div className="cop-toolbar-left">
          <div className="cop-live-badge">
            <span className="cop-live-dot" />
            LIVE · 30s refresh
          </div>
        </div>
        <div className="cop-toolbar-right">
          <button className="cop-btn" onClick={loadAllData}>
            <IconRefresh size={13} />Refresh
          </button>
          {available.length > 0 && (
            <Menu shadow="md" width={220}>
              <Menu.Target>
                <button className="cop-btn cop-btn-primary">
                  <IconPlus size={13} />Add Widget
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                {available.map(w => (
                  <Menu.Item key={w.id} leftSection={<w.icon size={14} />} onClick={() => addWidget(w.id)}>
                    {w.title}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
          <button
            className={`cop-btn${isEditing ? ' cop-btn-active' : ''}`}
            onClick={() => setIsEditing(!isEditing)}
          >
            <IconSettings size={13} />
            {isEditing ? 'Done' : 'Edit Layout'}
          </button>
          <button className="cop-btn cop-btn-icon" onClick={resetLayout} title="Reset layout">
            <IconMaximize size={13} />
          </button>
        </div>
      </div>

      <RGLComponent
        layouts={{ lg: layouts }}
        breakpoints={{ lg: 1200, md: 900, sm: 600 }}
        cols={{ lg: 12, md: 8, sm: 4 }}
        rowHeight={60}
        width={1200}
        isDraggable={isEditing}
        isResizable={isEditing}
        onLayoutChange={(layout: any) => { setLayouts(layout); save(LS_KEY, layout); }}
        draggableHandle=".cop-drag"
        compactType="vertical"
      >
        {activeWidgets.map(id => {
          const def = WIDGETS.find(w => w.id === id);
          const Comp = WIDGET_MAP[id];
          if (!def || !Comp) return null;
          const currentMode = widgetModes[id] || (def.modes?.[0] ?? 'stat');
          const hasModes = def.modes && def.modes.length > 1;
          const cardColor = def.color.startsWith('var(') ? '#4B3FFF' : def.color;

          return (
            <div key={id}>
              <div className="cop-widget-card" style={{ '--card-color': cardColor } as React.CSSProperties}>
                <div className="cop-widget-header">
                  <div className="cop-widget-header-left">
                    {isEditing && <div className="cop-drag"><IconGripVertical size={12} /></div>}
                    <span className="cop-widget-dot" style={{ background: cardColor }} />
                    <span className="cop-widget-title">{def.title}</span>
                  </div>
                  <div className="cop-widget-header-right">
                    {hasModes && (
                      <Menu shadow="md" width={140} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon size="xs" variant="subtle" color="#939598">
                            <IconChartBar size={11} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {def.modes.map(m => (
                            <Menu.Item key={m} onClick={() => setMode(id, m)}
                              style={{ fontWeight: currentMode === m ? 700 : 400, color: currentMode === m ? '#4B3FFF' : undefined }}>
                              {MODE_LABELS[m]}
                            </Menu.Item>
                          ))}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                    {isEditing && (
                      <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={() => removeWidget(id)}>
                        <IconTrash size={10} />
                      </ActionIcon>
                    )}
                  </div>
                </div>
                <div className="cop-widget-body">
                  <Comp mode={currentMode} />
                </div>
              </div>
            </div>
          );
        })}
      </RGLComponent>
    </div>
  );
}
